"use client";

import { useState, type FormEvent } from "react";
import { useRouter } from "next/navigation";
import { MapPin } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { StandaloneAddressAutocomplete, type ParsedAddress } from "@/components/inputs/address-autocomplete";
import { useLocationContext, type HeroLocation } from "@/components/home/context/location-context";
import { NEAR_YOU_REACH_MILES } from "@/components/home/sections/near-you/use-near-you-products";
import { buildListingSearchQuery } from "@/components/listing/utils/parse-search-params";
import { geocodePostalCode } from "@/lib/geocoding/geocode";
import { LANDING_PRODUCTS, LandingProductPicker, type LandingProductSlug } from "./landing-product-picker";
import { LANDING_FIELD_BAR_CLASSES } from "./landing-field-bar";

async function locationFor(address: ParsedAddress): Promise<HeroLocation | null> {
  if (address.lat !== undefined && address.lng !== undefined) {
    return { lat: address.lat, lng: address.lng, postalCode: address.zip, city: address.city, state: address.state };
  }
  const geo = address.zip ? await geocodePostalCode(address.zip) : null;
  return geo ? { lat: geo.latitude, lng: geo.longitude, postalCode: address.zip, city: geo.city, state: geo.state } : null;
}

const ZIP_PATTERN = /^\d{5}$/;

async function zipLocation(zip: string): Promise<HeroLocation | null> {
  const geo = await geocodePostalCode(zip);
  return geo ? { lat: geo.latitude, lng: geo.longitude, postalCode: zip, city: geo.city, state: geo.state } : null;
}

async function searchLocation(
  addressText: string,
  address: ParsedAddress | null,
  visitorLocation: HeroLocation | null,
): Promise<HeroLocation | null | "unplaced"> {
  const typed = addressText.trim();
  if (address) return (await locationFor(address)) ?? "unplaced";
  if (ZIP_PATTERN.test(typed)) return (await zipLocation(typed)) ?? "unplaced";
  return typed ? "unplaced" : visitorLocation;
}

function useFinder() {
  const router = useRouter();
  const { location: visitorLocation } = useLocationContext();
  const [product, setProduct] = useState<LandingProductSlug>(LANDING_PRODUCTS[0].slug);
  const [addressText, setAddressText] = useState("");
  const [address, setAddress] = useState<ParsedAddress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    let location: HeroLocation | null | "unplaced";
    try {
      location = await searchLocation(addressText, address, visitorLocation);
    } catch {
      setError("We couldn't look up that location. Try again.");
      return;
    } finally {
      setIsSubmitting(false);
    }
    if (location === "unplaced") {
      setError("Pick an address from the list or enter a 5-digit ZIP.");
      return;
    }
    const query = buildListingSearchQuery({
      productType: product,
      location: location ?? undefined,
      radiusMiles: location ? NEAR_YOU_REACH_MILES : undefined,
    });
    router.push(`/listing?${query}`);
  };
  const changeAddress = (display: string, parsed: ParsedAddress | null) => {
    setAddressText(display);
    setAddress(parsed);
  };
  return { product, setProduct, addressText, changeAddress, error, isSubmitting, submit };
}

function AddressSearch({ finder }: { finder: ReturnType<typeof useFinder> }) {
  return (
    <div className={LANDING_FIELD_BAR_CLASSES}>
      <MapPin aria-hidden className="ml-2 size-5 shrink-0 text-fg-muted" />
      <StandaloneAddressAutocomplete
        value={finder.addressText}
        onChange={finder.changeAddress}
        placeholder="Address or ZIP"
        aria-label="Delivery address"
        variant="ghost"
        hideIcon
        className="min-w-0 flex-1"
      />
      <Button type="submit" variant="primary" disabled={finder.isSubmitting}>
        Search
      </Button>
    </div>
  );
}

export function LandingFinderCard() {
  const finder = useFinder();
  return (
    <form
      onSubmit={(event) => void finder.submit(event)}
      aria-label="Find packaging near you"
      className="relative flex flex-col gap-4 rounded-3xl border border-border bg-surface p-4 shadow-2xl shadow-brand/15 sm:gap-5 sm:p-6"
    >
      <div className="flex items-center justify-between">
        <p className="text-base font-semibold">What are you looking for?</p>
        <span className="flex items-center gap-1.5 text-xs font-medium text-fg-muted">
          <span aria-hidden className="size-2 animate-pulse rounded-full motion-reduce:animate-none bg-brand" />
          Live listings
        </span>
      </div>
      <LandingProductPicker value={finder.product} onChange={finder.setProduct} />
      <AddressSearch finder={finder} />
      {finder.error && <p role="alert" className="text-sm text-destructive-strong">{finder.error}</p>}
    </form>
  );
}
